import { redirect } from 'react-router';
import { tokenStore } from '@/lib/http-core';
import { bootstrapAuth } from '@/lib/apiClient';
import type { AdminRole } from '@/lib/api-admin';

interface JwtPayload {
  roles?: readonly string[];
  twoFactorVerified?: boolean;
  exp?: number;
}

const decodeBase64Url = (segment: string): string => {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  if (typeof atob === 'function') return atob(padded + pad);
  return Buffer.from(padded + pad, 'base64').toString('binary');
};

export const parseJwtRoles = (
  token: string | null,
): { roles: readonly AdminRole[]; twoFactorVerified: boolean } | null => {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const json = decodeBase64Url(parts[1] ?? '');
    const decoded = JSON.parse(json) as JwtPayload;
    const roles = Array.isArray(decoded.roles)
      ? (decoded.roles.filter((r): r is AdminRole => typeof r === 'string') as readonly AdminRole[])
      : [];
    return { roles, twoFactorVerified: decoded.twoFactorVerified === true };
  } catch {
    return null;
  }
};

/**
 * Cold-start 복구: 메모리 토큰이 없으면 부팅 1회 silent refresh 를 await 한 뒤에만
 * /login 으로 보낸다. 새 탭/sessionStorage 소실 상황에서 유효한 refresh 쿠키로 세션을
 * 되살린다. 정상 네비게이션(토큰 보유)에선 bootstrapAuth 가 즉시 resolve 하므로 비용 0.
 * 진짜 익명 방문자는 refresh 가 null → /login (의도된 동작).
 */
const ensureSession = async (): Promise<string | null> => {
  if (tokenStore.token) return tokenStore.token;
  await bootstrapAuth();
  return tokenStore.token;
};

/**
 * QA-628 로그인 후 원래 화면으로 — 담당자 메일 [자세히 보기](/business-inquiries/{No})를 로그아웃 상태로 열면
 * /login?next=… 로 보내고, 로그인(2단계 인증 포함)을 마치면 그 화면으로 돌아온다. 대시보드·루트는 next 없이.
 */
export const loginPathFor = (request?: Request): string => {
  if (!request) return '/login';
  const url = new URL(request.url);
  const next = safeNextPath(url.pathname + url.search);
  return next && next !== '/' && !next.startsWith('/dashboard') ? `/login?next=${encodeURIComponent(next)}` : '/login';
};

/** 같은 사이트 경로만 받는다(//evil.com·/\evil.com 같은 외부 이동, /login 되돌이는 버림). */
export const safeNextPath = (raw: string | null | undefined): string | null =>
  raw && raw.startsWith('/') && !raw.startsWith('//') && !raw.startsWith('/\\') && !raw.startsWith('/login') ? raw : null;

export const authGuard = async (args?: { request: Request }): Promise<null> => {
  const token = await ensureSession();
  if (!token) throw redirect(loginPathFor(args?.request));
  return null;
};

export const rbacLoader =
  (required: ReadonlyArray<AdminRole>) =>
  async (args?: { request: Request }): Promise<null> => {
    const token = await ensureSession();
    if (!token) throw redirect(loginPathFor(args?.request));
    const claims = parseJwtRoles(token);
    if (!claims) throw redirect(loginPathFor(args?.request));
    const hasRole = required.some((r) => claims.roles.includes(r));
    if (!hasRole) throw redirect('/dashboard?denied=1');
    return null;
  };
